import { useEffect, useMemo, useRef, useState } from 'react';

interface CpmDateRangeFilterProps {
    dateFrom: string | null;
    dateTo: string | null;
    onChange: (next: { dateFrom: string | null; dateTo: string | null }) => void;
    label?: string;
    className?: string;
}

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const MONTH_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

/** Local calendar day → `YYYY-MM-DD`. */
function toYmd(d: Date): string {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYmd(ymd: string): Date {
    const parts = ymd.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function startOfDayIso(ymd: string): string {
    return `${ymd}T00:00:00.000Z`;
}

function endOfDayIso(ymd: string): string {
    return `${ymd}T23:59:59.999Z`;
}

function isoToYmd(iso: string | null): string | null {
    if (!iso) return null;
    return iso.slice(0, 10);
}

function fmtShort(ymd: string | null): string {
    if (!ymd) return '—';
    const d = parseYmd(ymd);
    return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function formatTrigger(fromIso: string | null, toIso: string | null): string {
    const from = isoToYmd(fromIso);
    const to = isoToYmd(toIso);
    if (!from && !to) return 'Any dates';
    if (from && to) return `${fmtShort(from)} – ${fmtShort(to)}`;
    if (from) return `${fmtShort(from)} – …`;
    return `… – ${fmtShort(to)}`;
}

function addDays(d: Date, n: number): Date {
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    next.setDate(next.getDate() + n);
    return next;
}

function startOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

type CalCell = { kind: 'out'; day: number } | { kind: 'in'; day: number; ymd: string };

function buildMonthCells(view: Date): CalCell[] {
    const year = view.getFullYear();
    const month = view.getMonth();
    const daysIn = new Date(year, month + 1, 0).getDate();
    // Monday-first: JS getDay() Sun=0 → lead Mon=0
    const lead = (new Date(year, month, 1).getDay() + 6) % 7;
    const prevLen = new Date(year, month, 0).getDate();
    const cells: CalCell[] = [];
    for (let i = lead - 1; i >= 0; i -= 1) {
        cells.push({ kind: 'out', day: prevLen - i });
    }
    for (let d = 1; d <= daysIn; d += 1) {
        cells.push({
            kind: 'in',
            day: d,
            ymd: `${year}-${pad2(month + 1)}-${pad2(d)}`,
        });
    }
    let trailing = 1;
    while (cells.length % 7 !== 0) {
        cells.push({ kind: 'out', day: trailing });
        trailing += 1;
    }
    return cells;
}

type PresetKey = 'today' | 'last7' | 'last30' | 'thisMonth';

function presetRange(key: PresetKey, today: Date): { from: string; to: string } {
    const to = toYmd(today);
    if (key === 'today') return { from: to, to };
    if (key === 'last7') return { from: toYmd(addDays(today, -6)), to };
    if (key === 'last30') return { from: toYmd(addDays(today, -29)), to };
    return { from: toYmd(startOfMonth(today)), to };
}

const PRESETS: Array<{ key: PresetKey; label: string }> = [
    { key: 'today', label: 'Today' },
    { key: 'last7', label: 'Last 7 days' },
    { key: 'last30', label: 'Last 30 days' },
    { key: 'thisMonth', label: 'This month' },
];

export default function CpmDateRangeFilter({
    dateFrom,
    dateTo,
    onChange,
    label = 'Flagged between',
    className = '',
}: CpmDateRangeFilterProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const hasValue = !!(dateFrom || dateTo);

    const appliedFrom = isoToYmd(dateFrom);
    const appliedTo = isoToYmd(dateTo);

    // Draft range — only committed to the parent (API) on Apply.
    const [draftFrom, setDraftFrom] = useState<string | null>(appliedFrom);
    const [draftTo, setDraftTo] = useState<string | null>(appliedTo);

    const [viewMonth, setViewMonth] = useState(() => {
        if (appliedFrom) return startOfMonth(parseYmd(appliedFrom));
        return startOfMonth(new Date());
    });

    const syncDraftFromApplied = () => {
        setDraftFrom(appliedFrom);
        setDraftTo(appliedTo);
        setViewMonth(
            appliedFrom
                ? startOfMonth(parseYmd(appliedFrom))
                : startOfMonth(new Date())
        );
    };

    const closeWithoutApply = () => {
        setOpen(false);
        syncDraftFromApplied();
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) {
                setOpen(false);
                setDraftFrom(appliedFrom);
                setDraftTo(appliedTo);
                setViewMonth(
                    appliedFrom
                        ? startOfMonth(parseYmd(appliedFrom))
                        : startOfMonth(new Date())
                );
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, appliedFrom, appliedTo]);

    const today = useMemo(() => {
        const n = new Date();
        return new Date(n.getFullYear(), n.getMonth(), n.getDate());
    }, [open]);

    const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);

    const pickDay = (ymd: string) => {
        // Two-click range (draft only — Apply commits):
        // 1) no start, or both ends already set → start a new range at pick
        // 2) start set, no end, pick before start → restart at pick
        // 3) start set, no end, pick on/after start → set end
        if (!draftFrom || (draftFrom && draftTo)) {
            setDraftFrom(ymd);
            setDraftTo(null);
            return;
        }
        if (ymd < draftFrom) {
            setDraftFrom(ymd);
            setDraftTo(null);
            return;
        }
        setDraftTo(ymd);
    };

    const applyPreset = (key: PresetKey) => {
        const range = presetRange(key, today);
        setDraftFrom(range.from);
        setDraftTo(range.to);
        setViewMonth(startOfMonth(parseYmd(range.from)));
    };

    const isPresetActive = (key: PresetKey) => {
        if (!draftFrom || !draftTo) return false;
        const range = presetRange(key, today);
        return draftFrom === range.from && draftTo === range.to;
    };

    const applyFilter = () => {
        onChange({
            dateFrom: draftFrom ? startOfDayIso(draftFrom) : null,
            dateTo: draftTo ? endOfDayIso(draftTo) : null,
        });
        setOpen(false);
    };

    const resetDraft = () => {
        setDraftFrom(null);
        setDraftTo(null);
    };

    const openMenu = () => {
        syncDraftFromApplied();
        setOpen(true);
    };

    const prevMonth = () => {
        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
    };
    const nextMonth = () => {
        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
    };

    const monthLabel = `${MONTH_NAMES[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`;

    return (
        <div
            ref={rootRef}
            className={[
                'relative flex flex-col flex-[1_1_180px] min-w-[165px] max-w-[240px]',
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            style={{ gap: 6 }}
        >
            <span
                className="uppercase text-[var(--cpm-text-secondary)]"
                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
            >
                {label}
            </span>
            <button
                type="button"
                onClick={() => (open ? closeWithoutApply() : openMenu())}
                className={[
                    'rounded-[10px] border-[1.5px] bg-white flex items-center text-left transition-all',
                    open || hasValue
                        ? 'border-[var(--cpm-brand-purple)] shadow-[var(--cpm-ring-focus)]'
                        : 'border-[var(--cpm-border-strong)]',
                ].join(' ')}
                style={{
                    height: 38,
                    paddingLeft: 11,
                    paddingRight: 11,
                    gap: 8,
                    transitionDuration: 'var(--cpm-transition-fast)',
                }}
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-text-tertiary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                >
                    <path d="M8 2v4" />
                    <path d="M16 2v4" />
                    <rect width="18" height="18" x="3" y="4" rx="2" />
                    <path d="M3 10h18" />
                </svg>
                <span
                    className={[
                        'flex-1 min-w-0 truncate tabular',
                        hasValue
                            ? 'text-[var(--cpm-text-primary)]'
                            : 'text-[var(--cpm-text-secondary)]',
                    ].join(' ')}
                    style={{ fontSize: 13, fontWeight: hasValue ? 700 : 600 }}
                >
                    {formatTrigger(dateFrom, dateTo)}
                </span>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-text-tertiary)"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0 ml-auto"
                >
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>

            {open ? (
                <div
                    className="absolute top-full right-0 z-30 bg-white border border-[var(--cpm-border-default)] cpm-animate-pop-in flex flex-col"
                    style={{
                        marginTop: 6,
                        width: 310,
                        borderRadius: 12,
                        boxShadow: 'var(--cpm-shadow-dropdown)',
                        padding: 14,
                        gap: 12,
                    }}
                >
                    <div className="flex flex-wrap" style={{ gap: 6 }}>
                        {PRESETS.map((p) => {
                            const active = isPresetActive(p.key);
                            return (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => applyPreset(p.key)}
                                    className="transition-all"
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 999,
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 800,
                                        border: `1.5px solid ${
                                            active
                                                ? 'var(--cpm-brand-purple)'
                                                : 'var(--cpm-border-strong)'
                                        }`,
                                        background: active ? 'var(--cpm-purple-tint-7)' : '#fff',
                                        color: active
                                            ? 'var(--cpm-brand-navy)'
                                            : 'var(--cpm-text-secondary)',
                                        transitionDuration: 'var(--cpm-transition-fast)',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (active) return;
                                        e.currentTarget.style.borderColor =
                                            'var(--cpm-brand-purple)';
                                        e.currentTarget.style.color = 'var(--cpm-brand-navy)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (active) return;
                                        e.currentTarget.style.borderColor =
                                            'var(--cpm-border-strong)';
                                        e.currentTarget.style.color = 'var(--cpm-text-secondary)';
                                    }}
                                >
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        style={{
                            height: 1,
                            background: 'var(--cpm-border-default)',
                        }}
                    />

                    <div className="flex items-center" style={{ gap: 10 }}>
                        <button
                            type="button"
                            onClick={prevMonth}
                            aria-label="Previous month"
                            className="inline-flex items-center justify-center transition-all"
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 8,
                                border: '1.5px solid var(--cpm-border-strong)',
                                color: 'var(--cpm-text-secondary)',
                                background: '#fff',
                                transitionDuration: 'var(--cpm-transition-fast)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--cpm-brand-purple)';
                                e.currentTarget.style.color = 'var(--cpm-brand-navy)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--cpm-border-strong)';
                                e.currentTarget.style.color = 'var(--cpm-text-secondary)';
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m15 18-6-6 6-6" />
                            </svg>
                        </button>
                        <span
                            className="flex-1 text-center text-[var(--cpm-text-primary)]"
                            style={{
                                fontSize: 13,
                                fontWeight: 800,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {monthLabel}
                        </span>
                        <button
                            type="button"
                            onClick={nextMonth}
                            aria-label="Next month"
                            className="inline-flex items-center justify-center transition-all"
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 8,
                                border: '1.5px solid var(--cpm-border-strong)',
                                color: 'var(--cpm-text-secondary)',
                                background: '#fff',
                                transitionDuration: 'var(--cpm-transition-fast)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--cpm-brand-purple)';
                                e.currentTarget.style.color = 'var(--cpm-brand-navy)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--cpm-border-strong)';
                                e.currentTarget.style.color = 'var(--cpm-text-secondary)';
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m9 18 6-6-6-6" />
                            </svg>
                        </button>
                    </div>

                    <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                        {WEEKDAYS.map((wd) => (
                            <span
                                key={wd}
                                className="text-center uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    padding: '2px 0 4px',
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {wd}
                            </span>
                        ))}
                        {cells.map((cell, idx) => {
                            if (cell.kind === 'out') {
                                return (
                                    <span
                                        key={`out-${idx}`}
                                        className="inline-flex items-center justify-center tabular text-[var(--cpm-text-tertiary)]"
                                        style={{
                                            height: 32,
                                            borderRadius: 8,
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            opacity: 0.45,
                                            cursor: 'default',
                                        }}
                                    >
                                        {cell.day}
                                    </span>
                                );
                            }

                            const inRange =
                                !!draftFrom &&
                                !!draftTo &&
                                cell.ymd > draftFrom &&
                                cell.ymd < draftTo;
                            const isEnd = cell.ymd === draftFrom || cell.ymd === draftTo;

                            return (
                                <button
                                    key={cell.ymd}
                                    type="button"
                                    onClick={() => pickDay(cell.ymd)}
                                    className="inline-flex items-center justify-center tabular transition-all"
                                    style={{
                                        height: 32,
                                        borderRadius: 8,
                                        fontSize: 12.5,
                                        fontWeight: isEnd ? 800 : 700,
                                        cursor: 'pointer',
                                        background: isEnd
                                            ? 'var(--cpm-brand-purple)'
                                            : inRange
                                              ? 'var(--cpm-purple-tint-7)'
                                              : 'transparent',
                                        color: isEnd
                                            ? '#fff'
                                            : inRange
                                              ? 'var(--cpm-brand-navy)'
                                              : 'var(--cpm-text-primary)',
                                        transitionDuration: 'var(--cpm-transition-fast)',
                                    }}
                                >
                                    {cell.day}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="flex items-center"
                        style={{
                            gap: 8,
                            padding: '9px 11px',
                            borderRadius: 10,
                            background: 'var(--cpm-surface-subtle)',
                        }}
                    >
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em' }}
                        >
                            From
                        </span>
                        <span
                            className="tabular text-[var(--cpm-text-primary)]"
                            style={{ fontSize: 12.5, fontWeight: 800 }}
                        >
                            {fmtShort(draftFrom)}
                        </span>
                        <span
                            className="text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 12, fontWeight: 800 }}
                        >
                            →
                        </span>
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em' }}
                        >
                            To
                        </span>
                        <span
                            className="tabular text-[var(--cpm-text-primary)]"
                            style={{ fontSize: 12.5, fontWeight: 800 }}
                        >
                            {draftTo ? fmtShort(draftTo) : 'Pick end date'}
                        </span>
                    </div>

                    <div className="flex items-center" style={{ gap: 8 }}>
                        <button
                            type="button"
                            onClick={resetDraft}
                            className="flex-1 text-center transition-all"
                            style={{
                                padding: '9px 0',
                                borderRadius: 10,
                                border: '1.5px solid var(--cpm-border-strong)',
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-secondary)',
                                background: '#fff',
                                transitionDuration: 'var(--cpm-transition-fast)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--cpm-brand-purple)';
                                e.currentTarget.style.color = 'var(--cpm-brand-navy)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--cpm-border-strong)';
                                e.currentTarget.style.color = 'var(--cpm-text-secondary)';
                            }}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={applyFilter}
                            className="flex-1 text-center transition-all"
                            style={{
                                padding: '9px 0',
                                borderRadius: 10,
                                background: 'var(--cpm-brand-navy)',
                                color: '#fff',
                                fontSize: 12.5,
                                fontWeight: 800,
                                transitionDuration: 'var(--cpm-transition-fast)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.filter = 'brightness(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.filter = 'none';
                            }}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
